import { Component, Injector, OnInit } from '@angular/core';
import { EngineerJobItemStatus, EngineerJobItemType } from '@app/main/modules/foundation/foundation.enums';
import { IEngineerJob, IEngineerJobBooking, IEngineerJobItem } from '@app/main/modules/foundation/foundation.interfaces';
import { EngineerJobItemService } from '@app/main/modules/foundation/_services/engineer-job-item.service';
import { TranslationService } from '@app/services/translation.service';
import { combineLatest, from } from 'rxjs';
import './engineer-job-item-list.component.scss';
import { NavigationService } from '@app/services/navigation.service';
import { RawParams } from "@uirouter/core";
import { VehicleService } from '@services/vehicle.service';
import { AccountService, IAccount } from '@services/account.service';
import { EngineerJobService } from '@app/main/modules/foundation/_services/engineer-job.service';
import { EngineerJobBookingService } from '@foundation/_services/engineer-job-booking.service';
import { IVehicle } from '@app/interfaces/IVehicle';
import copy from 'fast-copy';
import { FoundationNavigationModule } from '../../foundation.navigation';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface IEngineerJobWithJobItemsAndBookings extends IEngineerJob {
    jobItems: IEngineerJobItem[];
    jobBookings: IEngineerJobBooking[];

    jobAvailability: {startTime: Date, endTime: Date}
}

@UntilDestroy()
@Component({
    selector: 'engineer-job-item-list',
    template: require('./engineer-job-item-list.component.html'),
})
export class EngineerJobItemListComponent implements OnInit {

    jobs: IEngineerJobWithJobItemsAndBookings[] = [];
    displayJobs: IEngineerJobWithJobItemsAndBookings[] = [];

    loading: boolean;

    engineerJobItemType = EngineerJobItemType;

    foundJobItem: IEngineerJobItem = null;

    showIssueSidebar: boolean;
    selectedJobItem: IEngineerJobItem;
    selectedJob: IEngineerJob;
    
    constructor(
        private readonly injector: Injector,
        private readonly engineerJobService: EngineerJobService,
        private readonly engineerJobItemService: EngineerJobItemService,
        private readonly engineerJobBookingService: EngineerJobBookingService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly accountService: AccountService
    ) { }

    ngOnInit() {
        this.loadData(true);
    }
    
    onSearchChanged(text: string) {
        this.foundJobItem = null;
        this.displayJobs = copy(this.jobs);

        if (!text) {
            return;
        }

        this.filterJobItemsBySearchText(text);
        this.displayJobs = this.displayJobs.filter(job => job.jobItems.length > 0);

        this.setFoundJobItemIfApplicable();
    }

    createCommission(jobItemId: number): void {
        const rawParams: RawParams = {
            jobItemId: jobItemId,
        };

        const navigationService: NavigationService = this.injector.get(NavigationService);
        navigationService.go(FoundationNavigationModule.JobItemsCommissionNew, rawParams);
    }

    openIssueReport(jobItem: IEngineerJobItem): void {
        this.selectedJobItem = jobItem;
        this.selectedJob = this.jobs.find(job => job.Id === jobItem.JobId);
        this.showIssueSidebar = true;
    }

    closeIssueReport(): void {
        this.selectedJobItem = null;
        this.selectedJob = null;
        this.showIssueSidebar = false;
    }

    //--

    private loadData(loading = false): void {
        this.loading = loading;

        combineLatest([
            this.engineerJobItemService.getEngineerJobItems(),
            this.engineerJobBookingService.getEngineeringJobBookings(),
            from(this.vehicleService.getVehicles() as Promise<IVehicle[]>),
            from(this.accountService.getAccountListFromDatabase() as Promise<IAccount[]>),
            this.engineerJobService.getEngineerJobs()
        ]).pipe(untilDestroyed(this))
            .subscribe({
                next: ([jobItems, jobBookings, vehicles, accounts, jobs]) => {
                    jobItems = jobItems.filter(
                        (item) =>
                            item.Status !== EngineerJobItemStatus.Cancelled &&
                            item.Status !== EngineerJobItemStatus.Complete
                    );
                    this.jobs = jobs as IEngineerJobWithJobItemsAndBookings[];

                    this.mapVehiclesToJobItems(jobItems, vehicles);
                    this.mapJobs(jobItems, jobBookings, accounts, vehicles);
                    this.displayJobs = [...this.jobs];

                    this.loading = false;
                }
            });
    }

    private filterJobItemsBySearchText(text: string): void {
        const searchTextLower = text.toLowerCase(); // Convert the search text to lowercase

        this.displayJobs.forEach((job) => {
            job.jobItems = job.jobItems.filter((jobItem) => {
                const searchText =
                    (job.Id + (job.account?.CompanyName || '') + jobItem.Id + (jobItem.vehicle?.Reg || '') + (jobItem.vehicle?.ShortReg || '')).toLowerCase(); // Convert searchText to lowercase

                return searchText.includes(searchTextLower); // Compare in lowercase
            });
        });
    }

    private setFoundJobItemIfApplicable(): void {
        if (this.displayJobs.length === 1 && this.displayJobs[0].jobItems.length === 1) {
            this.foundJobItem = this.displayJobs[0].jobItems[0];
        }
    }

    private mapJobs(
        jobItems: IEngineerJobItem[],
        jobBookings: IEngineerJobBooking[],
        accounts: IAccount[],
        vehicles: IVehicle[],
    ) {
        this.jobs.forEach((job) => {
            job.jobBookings = jobBookings.filter(booking => booking.JobId === job.Id);
            this.assignJobAvailability(job);

            job.jobItems = jobItems.filter(item => item.JobId === job.Id);

            job.jobItems.forEach(item => {
                item.vehicle = vehicles.find(vehicle => item.VehicleId == vehicle.Id);
            });

            job.account = accounts.find(account => account.Id === job.AccountId);
        });

        this.jobs = this.jobs.filter((job) => job.jobItems.length > 0);
    }

    private mapVehiclesToJobItems(
        jobItems: IEngineerJobItem[],
        vehicles: IVehicle[]
    ) {
        jobItems.forEach((item) => {
            item.vehicle = !!item.VehicleId ? this.vehicleService.findVehicle(vehicles, item.VehicleId) : null;
        });
    }

    private assignJobAvailability(job: IEngineerJobWithJobItemsAndBookings): void {
        if (!job.jobBookings?.length) {
            return;
        }

        const booking = job.jobBookings[0];

        job.jobAvailability = {
            startTime: booking.StartUtc,
            endTime: booking.EndUtc
        };
    }
}
